import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Paperclip, Send, Trash2, Calendar, Flag, Globe, Users as UsersIcon, X } from 'lucide-react';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import api, { formatError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function TaskDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tasks/${id}`);
      setData(data);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Layout><div className="font-mono text-sm text-zinc-500">LOADING...</div></Layout>;
  const { task, updates } = data;

  const assignees = (task.assignees || []).map(a => a.name);

  const readFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, type: file.type, data_url: reader.result });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const addFiles = async (filesList) => {
    const arr = Array.from(filesList).slice(0, 4);
    const valid = arr.filter(f => f.size < 3 * 1024 * 1024);
    if (valid.length < arr.length) toast.error('Some files were skipped (3MB max each)');
    const readed = await Promise.all(valid.map(readFile));
    setFiles((prev) => [...prev, ...readed].slice(0, 4));
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/tasks/${id}/status`, { status });
      toast.success(`Status → ${status.replace('_', ' ')}`);
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!msg.trim() && files.length === 0) return;
    setSending(true);
    try {
      await api.post(`/tasks/${id}/updates`, { message: msg, attachments: files });
      setMsg(''); setFiles([]);
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSending(false); }
  };

  const canDelete = user?.role === 'boss' || task.created_by === user?.id;
  const handleDelete = async () => {
    if (!window.confirm('Delete this task and all updates?')) return;
    try {
      await api.delete(`/tasks/${id}`);
      toast.success('Task deleted');
      navigate('/');
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        <Link to="/" data-testid="back-link" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-zinc-600 hover:text-zinc-900">
          <ArrowLeft size={14} /> Back
        </Link>

        {/* Header */}
        <div className="bg-white border border-zinc-900 p-6">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <StatusBadge status={task.status} />
            <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-700"><Flag size={12} /> {task.priority}</div>
            {task.is_general ? (
              <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-700"><Globe size={12} /> General</div>
            ) : (
              <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-700"><UsersIcon size={12} /> {assignees.length} assigned</div>
            )}
            {task.due_date && (
              <div className="flex items-center gap-1 text-[10px] font-mono text-zinc-500"><Calendar size={12} /> Due {task.due_date}</div>
            )}
            {canDelete && (
              <button onClick={handleDelete} data-testid="delete-task-btn" className="ml-auto flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] text-red-600 hover:text-red-800">
                <Trash2 size={12} /> Delete
              </button>
            )}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter leading-tight">{task.title}</h1>
          {task.description && <p className="mt-3 text-zinc-700 whitespace-pre-wrap">{task.description}</p>}
          <div className="mt-4 pt-4 border-t border-zinc-200 flex flex-wrap items-center gap-3 text-xs text-zinc-600">
            <span>Posted by <span className="font-bold text-zinc-900">{task.created_by_name}</span></span>
            <span className="font-mono">·</span>
            <span className="font-mono">{new Date(task.created_at).toLocaleString()}</span>
            {!task.is_general && assignees.length > 0 && (
              <>
                <span className="font-mono">·</span>
                <span>Assigned to: <span className="font-bold text-zinc-900">{assignees.join(', ')}</span></span>
              </>
            )}
          </div>
          <div className="mt-4 flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mr-2">SET STATUS:</span>
            {['open', 'in_progress', 'resolved'].map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                data-testid={`set-status-${s}`}
                disabled={task.status === s}
                className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] border transition-colors ${task.status === s ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white border-zinc-300 hover:border-zinc-900'}`}
              >
                {s.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">PROGRESS THREAD · {updates.length}</div>
          <div className="space-y-3">
            {updates.length === 0 && (
              <div className="border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500">
                No updates yet. Post the first one below.
              </div>
            )}
            {updates.map((u) => (
              <div key={u.id} data-testid={`update-${u.id}`} className="bg-white border border-zinc-200 p-4">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <div className="w-8 h-8 bg-[#002FA7] text-white flex items-center justify-center font-bold text-sm">
                    {u.user_name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{u.user_name}</div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{u.user_role}</div>
                  </div>
                  <div className="ml-auto text-[10px] font-mono text-zinc-500">{new Date(u.created_at).toLocaleString()}</div>
                </div>
                <div className="text-sm text-zinc-800 whitespace-pre-wrap">{u.message}</div>
                {u.attachments?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {u.attachments.map((a, i) => {
                      const k = `${u.id}-${a.name || 'file'}-${i}`;
                      if (a.type?.startsWith('image/')) {
                        return (
                          <a key={k} href={a.data_url} target="_blank" rel="noreferrer" className="border border-zinc-300">
                            <img src={a.data_url} alt={a.name} className="max-h-40 object-cover" />
                          </a>
                        );
                      }
                      return (
                        <a key={k} href={a.data_url} download={a.name} className="flex items-center gap-2 border border-zinc-300 px-3 py-2 text-xs hover:border-zinc-900">
                          <Paperclip size={12} /> {a.name}
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Composer */}
        <form onSubmit={submit} className="bg-white border border-zinc-900 p-4 space-y-3" data-testid="update-composer">
          <textarea
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            placeholder="Post a daily update or answer..."
            rows={3}
            data-testid="update-input"
            className="w-full border border-zinc-300 focus:border-[#002FA7] outline-none px-3 py-2 bg-white resize-none"
          />
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {files.map((f, i) => (
                <div key={`${f.name}-${f.data_url?.slice(-8) || i}`} className="flex items-center gap-2 border border-zinc-300 px-2 py-1 text-xs">
                  <Paperclip size={12} /> <span className="truncate max-w-[140px]">{f.name}</span>
                  <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt" className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="file-input" />
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="attach-btn" className="flex items-center gap-2 border border-zinc-300 px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] hover:border-zinc-900">
              <Paperclip size={12} /> Attach
            </button>
            <button type="submit" disabled={sending} data-testid="post-update-btn" className="ml-auto flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#002088] disabled:opacity-60">
              <Send size={12} /> {sending ? 'Posting...' : 'Post Update'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
